/**
 * One-time legend explaining the tag colour system. Shown on first visit and
 * dismissable; persists via localStorage so returning users don't see it.
 */
import React, { useState } from "react";
import { X, Info } from "@phosphor-icons/react";

const STORAGE_KEY = "fa.tagLegendDismissed.v1";

const GROUPS = [
    {
        title: "Opportunity",
        color: "#9EE8B4",
        copy: "Greens & teals — safe picks, favourable matchups, buy-low chances.",
        examples: ["Safe Pick", "Favourite Opponent", "Buy Low"],
    },
    {
        title: "Quality",
        color: "#FFB800",
        copy: "Yellows & golds — premium trust, underlying-stat darlings, big-game talismans.",
        examples: ["Premium Trust", "Data Analyst", "Big-Game Player"],
    },
    {
        title: "Hot / Form",
        color: "#FF6B35",
        copy: "Oranges — players riding red-hot recent form or capable of high variance.",
        examples: ["Form Hunter", "Chaos Manager"],
    },
    {
        title: "Warning",
        color: "#FF2A2A",
        copy: "Reds — regression risks, bad matchups, FDR traps. Tread carefully.",
        examples: ["Regression Risk", "Bad Matchup", "FDR Trap"],
    },
    {
        title: "Differential",
        color: "#A855F7",
        copy: "Purples & blues — bold low-ownership picks, breakouts, dormant stars.",
        examples: ["Differential King", "Breakout Pick", "Cold Star"],
    },
];

export default function TagLegendBanner() {
    const [dismissed, setDismissed] = useState(() => {
        try {
            return typeof window !== "undefined"
                && window.localStorage.getItem(STORAGE_KEY) === "1";
        } catch (err) {
            console.warn("TagLegend: localStorage read failed", err);
            return false;
        }
    });

    const dismiss = () => {
        try { window.localStorage.setItem(STORAGE_KEY, "1"); }
        catch (err) { console.warn("TagLegend: localStorage write failed", err); }
        setDismissed(true);
    };

    if (dismissed) return null;

    return (
        <section
            className="border border-fa-violet/30 bg-gradient-to-br from-fa-violet/10 via-black to-black p-4 relative"
            data-testid="tag-legend-banner"
        >
            <button
                type="button"
                onClick={dismiss}
                data-testid="tag-legend-dismiss"
                className="absolute top-2 right-2 text-white/40 hover:text-white p-1"
                aria-label="Dismiss legend"
            >
                <X size={14} weight="bold" />
            </button>

            <div className="flex items-center gap-2 mb-3">
                <Info size={16} weight="fill" className="text-fa-cream" />
                <h3 className="font-display font-black text-base uppercase tracking-tight">
                    Reading Player Tags
                </h3>
                <span className="font-mono text-[10px] text-white/40">
                    · hover any tag for the full description
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {GROUPS.map((g) => (
                    <div
                        key={g.title}
                        className="border border-white/10 bg-black/40 p-3"
                        data-testid={`tag-legend-${g.title.toLowerCase().replace(/[^a-z]/g, "")}`}
                    >
                        <div
                            className="font-mono text-[10px] uppercase tracking-widest font-bold mb-1"
                            style={{ color: g.color }}
                        >
                            ● {g.title}
                        </div>
                        <p className="font-mono text-[11px] text-white/70 leading-snug mb-2">
                            {g.copy}
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {g.examples.map((ex) => (
                                <span
                                    key={ex}
                                    className="font-tag text-[9px] uppercase px-1 py-0.5 leading-none"
                                    style={{ background: g.color + "33", color: g.color }}
                                >
                                    {ex}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}
